import { useState, useEffect, useRef, useCallback } from "react";
import { Camera, X, Check, Lightning, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function shutterBeep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "square";
    osc.frequency.value = 1400;
    gain.gain.setValueAtTime(0.06, ctx.currentTime);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.08);
    osc.stop(ctx.currentTime + 0.08);
  } catch {
    /* ignore */
  }
}

// Live camera: 1 tik = foto, geen bevestiging. Camera blijft open voor volgende label.
export const FotoCameraModal = ({ open, onClose, onCapture, aantal = 0, bezig = 0, labels = 0 }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  const [sessieCount, setSessieCount] = useState(0);
  const [laatste, setLaatste] = useState(null);
  const [torchOn, setTorchOn] = useState(false);
  const [torchBeschikbaar, setTorchBeschikbaar] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    setReady(false);
    setSessieCount(0);
    setLaatste(null);
    setTorchOn(false);
    setTorchBeschikbaar(false);

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled || !videoRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", "true");
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => {});
        const track = stream.getVideoTracks()[0];
        const caps = track?.getCapabilities?.() || {};
        if (caps.torch) setTorchBeschikbaar(true);
        setReady(true);
      } catch {
        if (!cancelled) setError("Camera niet beschikbaar. Geef toestemming voor camera-toegang in je browser.");
      }
    })();

    return () => {
      cancelled = true;
      try {
        streamRef.current?.getTracks().forEach((t) => t.stop());
      } catch {
        /* ignore */
      }
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [open]);

  const toggleTorch = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((v) => !v);
    } catch {
      /* ignore */
    }
  }, [torchOn]);

  const maakFoto = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || busy) return;
    setBusy(true);
    try {
      const canvas = document.createElement("canvas");
      const maxZijde = 1600;
      const schaal = Math.min(1, maxZijde / Math.max(video.videoWidth, video.videoHeight));
      canvas.width = Math.round(video.videoWidth * schaal);
      canvas.height = Math.round(video.videoHeight * schaal);
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.75));
      if (!blob) return;
      const file = new File([blob], `label-${Date.now()}.jpg`, { type: "image/jpeg" });
      shutterBeep();
      setFlash(true);
      setTimeout(() => setFlash(false), 140);
      setLaatste(URL.createObjectURL(blob));
      setSessieCount((c) => c + 1);
      onCapture(file);
    } finally {
      setTimeout(() => setBusy(false), 250);
    }
  }, [busy, onCapture]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="foto-camera-modal">
      {/* Topbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/80 text-white">
        <div className="flex items-center gap-2 text-sm font-bold">
          <Camera size={18} weight="bold" />
          Label scannen
          {aantal > 0 && (
            <Badge className="bg-white text-black ml-1" data-testid="camera-foto-teller">
              {aantal}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {torchBeschikbaar && (
            <button
              type="button"
              onClick={toggleTorch}
              className={`p-2 rounded-full ${torchOn ? "bg-yellow-400 text-black" : "bg-white/15 text-white"}`}
              data-testid="camera-torch-btn"
            >
              <Lightning size={18} weight="fill" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full bg-white/15 text-white"
            data-testid="camera-close-btn"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
      </div>

      {/* Viewfinder */}
      <div className="relative flex-1 overflow-hidden">
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted autoPlay />
        {flash && <div className="absolute inset-0 bg-white/80" />}
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-white">
            {error}
          </div>
        ) : (
          <>
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-[82%] h-[46%] border-2 border-white/70 rounded-sm" />
            </div>
            {!ready && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm gap-2">
                <ArrowsClockwise size={18} className="animate-spin" /> Camera starten...
              </div>
            )}
            {laatste && (
              <img
                src={laatste}
                alt=""
                className="absolute bottom-3 left-3 w-16 h-16 object-cover rounded-sm border-2 border-white/80"
                data-testid="camera-laatste-foto"
              />
            )}
            <div className="absolute top-3 left-0 right-0 text-center text-white/80 text-xs px-6">
              1 tik = direct foto · de AI leest elke foto meteen
            </div>
            <div
              className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-3 py-2 rounded-sm font-bold flex items-center gap-2"
              data-testid="camera-ai-status"
            >
              {bezig > 0 ? (
                <>
                  <ArrowsClockwise size={14} className="animate-spin" /> AI leest {bezig}...
                </>
              ) : (
                <>
                  <Check size={14} weight="bold" className="text-emerald-400" /> {labels} etiket
                  {labels !== 1 ? "ten" : ""} gelezen
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Shutter */}
      <div className="bg-black/90 px-6 py-5 flex items-center justify-between">
        <div className="w-24 text-white text-xs">
          {sessieCount > 0 ? `${sessieCount} genomen` : ""}
        </div>
        <button
          type="button"
          onClick={maakFoto}
          disabled={!ready || busy}
          className="w-20 h-20 rounded-full bg-white border-[5px] border-white/40 active:scale-90 transition-transform disabled:opacity-40"
          aria-label="Foto maken"
          data-testid="camera-shutter-btn"
        />
        <div className="w-24 flex justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            className="bg-white/10 text-white border-white/40 hover:bg-white/20"
            data-testid="camera-klaar-btn"
          >
            <Check size={16} weight="bold" className="mr-1" /> Klaar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FotoCameraModal;
